// Human in the loop — in-memory mock store. No backend.
import { useSyncExternalStore } from 'react';
import type { DoneItem, GroupKey, HitlItem, Try, WaitingPage } from './types';
import { MOCK_NOW } from '@/components/lists';
import { buildDone, buildItems, buildWaitingPages } from './fixtures';
import { CALIFORNIA_TODAY_ISO, REMIND_CUTOFF_DAYS, buildCreditItems } from './credit';
import { addDays, fmtMonthDay, fmtMonthDayYear } from './dates';
import { PMS } from '@/features/shared/pms';

export const WORK_MS = 1500;
const ACTOR = 'Ava Thompson (Lone Star Billing)';
/** Patient credit rows are closed by the practice's admin (H8 Done list). */
const CREDIT_ACTOR = 'Ava Thompson (admin)';

type Scenario = '' | 'empty' | 'photo-unreadable' | 'california';

interface State {
  items: HitlItem[];
  done: DoneItem[];
  scenario: Scenario;
}

const listeners = new Set<() => void>();
let version = 0;
function emit(): void {
  version += 1;
  for (const l of listeners) l();
}
const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

function readScenario(): Scenario {
  try {
    const raw = new URLSearchParams(window.location.search).get('scenario') ?? '';
    if (raw === 'empty' || raw === 'photo-unreadable' || raw === 'california') return raw;
  } catch {
    /* no window */
  }
  return '';
}

function initState(): State {
  const scenario = readScenario();
  // Credit ids resolve in every scenario; they only join the list under ?scenario=california.
  const items = [...(scenario === 'empty' ? [] : buildItems()), ...buildCreditItems(scenario === 'california')];
  return { items, done: buildDone(), scenario };
}

const state: State = initState();

export function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
export function getVersion(): number {
  return version;
}
export function useHitlVersion(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

const GROUP_ORDER: GroupKey[] = ['now', 'tomorrow', 'week', 'later'];
const GROUP_LABEL: Record<GroupKey, string> = { now: 'Today', tomorrow: 'Before tomorrow', week: 'This week', later: 'Later' };

export function getScenarioSync(): Scenario {
  return state.scenario;
}
/** ?scenario=california puts the practice in California and today on Nov 16, 2026. */
export function getPracticeStateSync(): { stateName: string; todayIso: string | null } {
  return state.scenario === 'california' ? { stateName: 'California', todayIso: CALIFORNIA_TODAY_ISO } : { stateName: 'Texas', todayIso: null };
}
/** A credit item waiting to come back (told the patient / remind me) stays out of the list and the counts. */
function isWaiting(i: HitlItem): boolean {
  const c = i.credit;
  return c?.snoozedUntil !== undefined && c.snoozedUntil > c.asOfIso;
}
export function getItemsSync(): HitlItem[] {
  return state.items.filter((i) => !i.removed && !i.unlisted && !isWaiting(i));
}
export function getItemSync(id: string): HitlItem | null {
  return state.items.find((i) => i.id === id) ?? null;
}
export function groupedSync(): { key: GroupKey; label: string; items: HitlItem[] }[] {
  const open = getItemsSync();
  return GROUP_ORDER.map((key) => ({ key, label: GROUP_LABEL[key], items: open.filter((i) => i.group === key) })).filter((g) => g.items.length > 0);
}
export function openCountSync(): number {
  return getItemsSync().length;
}
export function useHitlCount(): number {
  return useSyncExternalStore(subscribe, openCountSync, openCountSync);
}
export function getDoneSync(): DoneItem[] {
  return state.done;
}
export function getWaitingPagesSync(): WaitingPage[] {
  return buildWaitingPages().filter((p) => p.count > 0);
}

const WORKING: Record<string, string> = {
  patient: 'ELVA is checking her coverage…',
  access: 'ELVA is testing the new password…',
  dentist: 'ELVA is writing the narrative…',
  call: 'ELVA is reading your note and taking the next step…',
  money: 'ELVA is handling the check with Guardian…',
  claim: 'ELVA is saving your decision…',
  document: 'ELVA is posting the check…',
  credit: `ELVA is checking ${PMS}…`,
};

/** Save an answer; drives §4.2. */
export async function* answerItem(id: string): AsyncGenerator<{ working: string } | { done: { findings: string[]; by: string } } | { stillOpen: Try }> {
  const item = state.items.find((i) => i.id === id);
  if (!item) return;
  yield { working: WORKING[item.kind] ?? 'ELVA is working…' };
  await wait(WORK_MS);

  if (state.scenario === 'photo-unreadable' && item.kind === 'patient') {
    const t: Try = { time: 'Oct 15, 9:15 AM', channel: 'Photo check', result: 'Photo too blurry to read the plan; ELVA texted Patricia for a clearer one.', ok: false, logEntryId: 'log-doc-guardian' };
    item.tries = [...item.tries, t];
    emit();
    yield { stillOpen: t };
    return;
  }

  item.removed = 'done';
  item.doneBy = ACTOR;
  item.doneTime = '9:14 AM';
  item.doneLabel = `Answered by ${ACTOR} at 9:14 AM.`;
  state.done.unshift({ id: `done-${item.id}`, kindLabel: item.kindLabel, title: item.title, by: ACTOR, outcome: item.doneFindings?.[0] ?? 'Done.' });
  emit();
  yield { done: { findings: item.doneFindings ?? [], by: ACTOR } };
}

/** C1: the patient has checked in — move the item to Today (HITL_TASK_CATALOG §3 C1). */
export function checkIn(id: string): void {
  const item = state.items.find((i) => i.id === id);
  if (!item) return;
  item.group = 'now';
  item.deadlineText = 'Checked in';
  item.deadlineWarn = true;
  emit();
}

export function cantDo(id: string, reason: string, note?: string): void {
  const item = state.items.find((i) => i.id === id);
  if (!item) return;
  item.removed = 'done';
  item.doneLabel = 'Closed without an answer';
  const trimmedNote = note?.trim() ?? '';
  const detail = trimmedNote !== '' ? trimmedNote : reason;
  state.done.unshift({ id: `done-${item.id}`, kindLabel: item.kindLabel, title: item.title, by: ACTOR, outcome: `Closed without an answer: ${detail}` });
  emit();
}

/**
 * "Decide about a claim" (D147/D159). Keep going schedules more rounds; Stop
 * writes it off with the reason. Either way the item closes here.
 */
export function decideClaim(id: string, mode: 'keep' | 'stop', reason?: string): void {
  const item = state.items.find((i) => i.id === id);
  if (!item) return;
  const next = item.claimDecide?.checkDate ?? '';
  const outcome =
    mode === 'keep'
      ? `${ACTOR.split(' (')[0] ?? ACTOR} chose Keep going. ELVA runs ${String(item.claimDecide?.moreRounds ?? 3)} more rounds${next ? `, next check ${next}` : ''}.`
      : `Stopped by ${ACTOR.split(' (')[0] ?? ACTOR}: ${reason ?? 'Write it off'}.`;
  item.removed = 'done';
  item.claimDecision = {
    mode,
    ...(reason ? { reason } : {}),
    by: ACTOR.split(' (')[0] ?? ACTOR,
    dateLabel: `${MOCK_NOW.toLocaleString('en-US', { month: 'short' })} ${String(MOCK_NOW.getDate())}`,
  };
  item.doneLabel = mode === 'keep' ? `Answered by ${ACTOR}` : 'Closed without an answer';
  state.done.unshift({ id: `done-${item.id}`, kindLabel: item.kindLabel, title: item.title, by: ACTOR, outcome });
  emit();
}

/**
 * Insurance A/R: a claim reached the follow-up limit, so it gets its own
 * "Decide about a claim" item (D117/D159 — one item per claim). Idempotent on
 * `id`; returns the id so A/R links to THIS claim's item.
 */
export function addClaimDecideItem(input: {
  id: string;
  patient: string;
  patientFirst: string;
  payer: string;
  amount: number;
  days: number;
  sent: string;
  claimNo: string;
  rounds: number;
  tries: Try[];
  checkDates: string[];
}): string {
  if (state.items.some((i) => i.id === input.id)) return input.id;
  const amt = `$${input.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const whole = `$${Math.round(input.amount).toLocaleString('en-US')}`;
  const more = input.checkDates.length;
  state.items.push({
    id: input.id,
    kind: 'claim',
    kindLabel: 'Decide about a claim',
    group: 'week',
    title: `${input.patient} · ${input.payer} hasn’t paid after ${String(input.days)} days`,
    metaLine: `${input.payer} · ${amt} · sent ${input.sent} · claim ${input.claimNo}`,
    deadlineText: 'At your follow-up limit',
    deadlineWarn: false,
    why: `ELVA followed up ${String(input.rounds)} times, the limit you set, and ${input.payer} still hasn’t paid. ${String(input.days)} days since the claim was sent. Someone decides: let ELVA keep going, or stop and write it off.`,
    tries: input.tries,
    claimDecide: {
      amount: amt,
      checkDate: input.checkDates[0] ?? '',
      dates: input.checkDates,
      moreRounds: more,
      stopReasons: ['Write it off', 'The patient will pay it', 'Billed to another insurance', 'Something else'],
      patientPayHelper: `ELVA moves ${whole} to ${input.patientFirst}’s balance in ${PMS} and logs it. Collecting it is ELVA Core’s job.`,
      patientFirst: input.patientFirst,
    },
    savePrimary: 'Keep going',
    nextMeta: `Keep going runs ${String(more)} more rounds, then it comes back here. Stop writes it off in ${PMS}.`,
    cantDo: {
      title: 'Not sure yet?',
      reasons: ['Need to ask the office', 'Something else'],
      notice: 'ELVA keeps this claim out of new follow-ups until someone decides.',
    },
    history: [`sent ${input.sent} · ${String(input.rounds)} follow-ups by ELVA · came here ${MOCK_NOW.toLocaleString('en-US', { month: 'short' })} ${String(MOCK_NOW.getDate())}`],
    doneFindings: [`ELVA has your decision and is handling ${input.patient}’s claim.`],
  });
  emit();
  return input.id;
}

// ── Patient credit (D198, H8) ───────────────────────────────────────

export type CreditChoice = 'refunded' | 'told' | 'keep' | 'used';

/**
 * Save the answer on a Patient credit item. ELVA only checks the PMS and writes a
 * note; it never refunds, moves or applies the money.
 * - refunded / used: ELVA checks the PMS and closes the item.
 * - told: the item comes back 30 days after the date given (if no refund is in the PMS).
 * - keep: ELVA writes a dated note on the account and stops reminding; closes.
 */
export async function* answerCredit(
  id: string,
  answer: { choice: CreditChoice; toldIso?: string; askedNote?: string },
): AsyncGenerator<{ working: string } | { done: { findings: string[]; by: string } } | { waiting: { until: string; note: string } }> {
  const item = state.items.find((i) => i.id === id);
  const c = item?.credit;
  if (!item || !c) return;
  yield { working: answer.choice === 'keep' ? `ELVA is writing the note on ${c.patientFirst}’s account…` : `ELVA is checking ${PMS}…` };
  await wait(WORK_MS);

  const today = c.asOfIso;
  const todayMd = fmtMonthDay(today);

  if (answer.choice === 'told') {
    const told = answer.toldIso ?? today;
    // 30 days after the date told; never in the past, so it can't reopen at once.
    const back = [addDays(told, 30), addDays(today, 1)].reduce((a, b) => (a > b ? a : b));
    const note = `ELVA checks ${PMS} for the refund. If it isn’t there by ${fmtMonthDay(back)}, this comes back here.`;
    c.snoozedUntil = back;
    c.snoozeNote = note;
    item.history = [...item.history, `told ${fmtMonthDay(told)} by ${CREDIT_ACTOR.split(' (')[0] ?? CREDIT_ACTOR} · back ${fmtMonthDay(back)}`];
    emit();
    yield { waiting: { until: back, note } };
    return;
  }

  const closing: Record<Exclude<CreditChoice, 'told'>, { outcome: string; findings: string[] }> = {
    refunded: {
      outcome: `Refunded in ${PMS} ${todayMd}`,
      findings: [`ELVA checked ${PMS}: the ${c.amount} refund to ${c.patientFirst} is there. Closed.`],
    },
    used: {
      outcome: `Used on another visit · ELVA checked ${PMS} ${todayMd}`,
      findings: [`ELVA checked ${PMS}: ${c.patientFirst}’s ${c.amount} was used on a visit. Closed.`],
    },
    keep: {
      outcome: `Kept as credit at ${c.patientFirst}’s request · note written in ${PMS} ${todayMd}`,
      findings: [
        `ELVA wrote a dated note on ${c.patientFirst}’s account in ${PMS}: “${fmtMonthDayYear(today)}: ${c.patientName} asked to keep ${c.amount} as credit (${(answer.askedNote ?? '').trim()}). Recorded by ${CREDIT_ACTOR.split(' (')[0] ?? CREDIT_ACTOR}.”`,
        'ELVA stops reminding about this credit.',
      ],
    },
  };
  const chosen = closing[answer.choice];
  item.removed = 'done';
  item.doneBy = CREDIT_ACTOR;
  item.doneLabel = `Answered by ${CREDIT_ACTOR}.`;
  item.doneFindings = chosen.findings;
  state.done.unshift({
    id: `done-${item.id}`,
    kindLabel: item.kindLabel,
    title: `${c.patientName}’s ${c.amount} credit`,
    by: CREDIT_ACTOR,
    outcome: chosen.outcome,
    dateIso: today,
    dateLabel: fmtMonthDayYear(today),
  });
  emit();
  yield { done: { findings: chosen.findings, by: CREDIT_ACTOR } };
}

/** "Remind me in 2 weeks": the item leaves the list and returns in 14 days, never later than 7 days before a California deadline. */
export function remindCreditLater(id: string): void {
  const item = state.items.find((i) => i.id === id);
  const c = item?.credit;
  if (!item || !c?.remindOffered) return;
  let back = addDays(c.asOfIso, 14);
  if (c.tellByIso) {
    const latest = addDays(c.tellByIso, -REMIND_CUTOFF_DAYS);
    if (latest < back) back = latest;
  }
  c.snoozedUntil = back;
  c.snoozeNote = `ELVA brings this back on ${fmtMonthDay(back)}.`;
  item.history = [...item.history, `reminded ${fmtMonthDay(c.asOfIso)} · back ${fmtMonthDay(back)}`];
  emit();
}
